import { useState, useCallback, useMemo } from 'react';
import { Category } from 'types/entity/category';
import { MenuState, NavigationActions, NavigationLevel } from '../types';

const INITIAL_MENU_STATE: MenuState = {
  isOpen: false,
  navigationStack: [],
  currentCategory: null,
};

export const useMenuNavigation = () => {
  const [menuState, setMenuState] = useState<MenuState>(INITIAL_MENU_STATE);

  const actions: NavigationActions = {
    openMenu: useCallback(() => {
      setMenuState((prev) => ({
        ...prev,
        isOpen: true,
      }));
    }, []),

    closeMenu: useCallback(() => {
      setMenuState(INITIAL_MENU_STATE);
    }, []),

    navigateToCategory: useCallback((category: Category) => {
      setMenuState((prev) => {
        const newLevel: NavigationLevel = {
          category,
          depth: prev.navigationStack.length,
        };

        return {
          ...prev,
          navigationStack: [...prev.navigationStack, newLevel],
          currentCategory: category,
        };
      });
    }, []),

    navigateBack: useCallback(() => {
      setMenuState((prev) => {
        if (prev.navigationStack.length === 0) return prev;

        const newStack = prev.navigationStack.slice(0, -1);
        const currentCategory = newStack.length > 0 ? newStack[newStack.length - 1].category : null;

        return {
          ...prev,
          navigationStack: newStack,
          currentCategory,
        };
      });
    }, []),

    navigateToRoot: useCallback(() => {
      setMenuState((prev) => ({
        ...prev,
        navigationStack: [],
        currentCategory: null,
      }));
    }, []),
  };

  // Computed values
  const isAtRoot = menuState.navigationStack.length === 0;
  const canNavigateBack = menuState.navigationStack.length > 0;
  const currentDepth = menuState.navigationStack.length;

  const navigationBreadcrumb = useMemo(
    () => menuState.navigationStack.map((level) => level.category?.name || '').filter(Boolean),
    [menuState.navigationStack],
  );

  return {
    menuState,
    actions,
    isAtRoot,
    canNavigateBack,
    currentDepth,
    navigationBreadcrumb,
  };
};
